'use client';

/**
 * coπ in the BB app — a round, floating microphone you can drag anywhere
 * (Gordon 2026-10-31: "a floating copi microphone icon that you can drag move
 * anywhere on the screen. Round floating button. With an old microphone style
 * icon and translucent bg and outline and text in same theme color").
 *
 * PRESS AND HOLD to talk, release to act — like a voice-message button
 * (Gordon 2026-10-31); on a keyboard, hold M. While held:
 *   • speech recognition (the browser's own, free) hears the words — no card
 *     while held, the VU + ring show it listens. On RELEASE everything heard (final words, or the
 *     last interim guess when the recogniser hadn't finalised yet) goes
 *     through lib/copi-commands once — pages, player, "play <song / album /
 *     artist / genre / 90 bpm / playlist / video / something random>"
 *     (lib/copi-media + services/copi-library), search, chat, favorites,
 *     hero. Anything unrecognised becomes a search. Recognition starts INSIDE
 *     the press itself (iOS only allows that).
 *   • the microphone's REAL level drives the main VU meters
 *     (window.__bbMicDb / __bbMicOn) — the only honest way to meter sound the
 *     page can't tap (Gordon 2026-10-31: "the microphone input should trigger
 *     main vu meters"). NOT on Android: there the recogniser runs in the
 *     Google app, and a second mic stream opened by the page takes the mic
 *     from it — it heard only silence ("Didn't catch that", Gordon
 *     2026-10-31). The words win; Android's VU stays still while listening.
 *     iPhone/iPad open the level stream once the recogniser has the mic; if
 *     that device then can't share it, this device stops metering
 *     (PHONE_LEVEL_KEY).
 * Default spot: right above the chat button, following it when the menu side
 * flips (Gordon 2026-10-31). Drag (move more than a few px) = reposition; that
 * spot is remembered on this device. Hidden while presenting full screen.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/auth-context';
import { useMediaPlayer } from '@/contexts/media-player-context';
import { NAV_PAGES } from '@/lib/menu-config';
import { COPI_BROWSE_PAGES, DISCOGS_LINK, isCopiNegation, leadingCopiVerb, matchCopiCommand, phraseKey, speechLang, type CopiAction, type MediaIntent, type ShowKind } from '@/lib/copi-commands';
import { findEntity, findPlace, genreKey, pickMedia, styleNamed, type CopiLibrary, type CopiPlace } from '@/lib/copi-media';
import {
  aliasIntent, applyCopiAliases, contradictsCopi, copiLang, lookupCopiMemory, COPI_CONFIRM_MS, type CopiLayer,
} from '@/lib/copi-learning';
import { loadCopiLibrary, loadCopiPlaces } from '@/services/copi-library';
import {
  askCopiAi, countCopiLayer, loadKnowledge, logCopiMiss, rememberCopi, teachCopi, type CopiAiAction, type CopiKnowledge,
} from '@/services/copi-ai';
import { encodeWav16k } from '@/lib/wav';
import { TUNINGS, A4_MIN, A4_MAX, nearestTuning, type TuningPreset } from '@/lib/music/tunings';
import { getPlaylistTracks } from '@/services/playlists';
import type { MediaTrack } from '@/contexts/media-player-context';
import { useOptionalUserLayoutPrefs } from '@/contexts/user-layout-prefs-context';
import { MAIN_VOICES, type CopiVoiceLine } from '@/lib/copi-voices';
import { copiVoiceElement, copiVoiceUrl, playCopiLine } from '@/lib/copi-voice-client';
import { getLatestMessage, sendMessage, type ChatMessage } from '@/services/chat';
import { createSong } from '@/services/songs';
import { epxCommand, epxCommandList, epxMounted, epxReady } from '@/lib/epx-bridge';
import { fetchWithAuth } from '@/lib/fetch-with-auth';

/** Creative tools coπ opens by name ("open songwriter", "öppna DJ", "abre el
 *  editor de video", "ouvre EP-X"); the last name is the one coπ says back. */
// Nav labels of the browse pages, for their name in the UI language.
const BROWSE_NAV_KEYS: Record<string, string> = {
  '/artists': 'artists', '/releases': 'releases', '/videos': 'videos', '/labels': 'labels', '/playlists': 'playlists', '/charts': 'charts',
};

const CREATIVE_APPS: { href: string; navKey?: string; names: string[] }[] = [
  { href: '/studio/songwriter', navKey: 'songwriter', names: ['songwriting', 'song writer', 'song writing', 'songbook', 'låtskrivning', 'låtskrivare', 'compositor', 'composition', 'Songwriter'] },
  { href: '/studio/recorder', navKey: 'recorder', names: ['voice recorder', 'voice memo', 'voice memos', 'inspelning', 'grabadora', 'enregistreur', 'Recorder'] },
  { href: '/studio/podcast', names: ['podcast studio', 'podcasts', 'podd', 'poddstudio', 'Podcast'] },
  { href: '/studio/jam-session', navKey: 'jamSession', names: ['jam', 'jamsession', 'Jam Session'] },
  { href: '/studio/live', names: ['live studio', 'go live', 'livestream', 'live stream', 'Live'] },
  { href: '/studio/publisher', names: ['publishing studio', 'Publisher'] },
  { href: '/dj', names: ['dj', 'dj mixer', 'decks', 'vj', 'dj vj', 'BB DJ'] },
  { href: '/film-maker', navKey: 'filmMaker', names: ['filmmaker', 'film', 'video editor', 'film editor', 'videoredigerare', 'editor de video', 'montage video', 'Film Maker'] },
  { href: '/artwork-studio', navKey: 'artworkStudio', names: ['artwork', 'cover designer', 'design studio', 'Artwork Studio'] },
  { href: '/studio/epx', names: ['epx', 'ep x', 'ep 133', 'groovebox', 'sampler', 'beat maker', 'beatmaker', 'EP-X'] },
  { href: '/bookkeeping', navKey: 'bookkeeping', names: ['books', 'my books', 'bokföring', 'bokföringen', 'contabilidad', 'comptabilité', 'Bookkeeping'] },
];

// v2 (Gordon 2026-11-01: "centre the mic right above the chat button, as default"):
// everyone starts from the default spot again; a new drag is remembered as before.
const POS_KEY = 'bb-copi-pos-v2';
const PHONE_LEVEL_KEY = 'bb-copi-phone-level';
/** Until the chat button is measured (it is 50px on desktop, 56px on phones). */
const DEFAULT_SIZE = 50;

/** A vintage studio microphone: round grille on a yoke and stand. */
function VintageMic({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <rect x="7.5" y="2" width="9" height="11.5" rx="4.5" />
      <path d="M7.8 5.6h8.4M7.5 8h9M7.5 10.4h9M7.8 12.4h8.4" strokeWidth="1.1" />
      <path d="M5 9.5v1a7 7 0 0 0 14 0v-1" />
      <path d="M12 17.5V21M8.5 21h7" />
    </svg>
  );
}

export function CopiMic() {
  const t = useTranslations('copi');
  const tNav = useTranslations('nav');
  const tCommon = useTranslations('common');
  const tTuner = useTranslations('tuner');
  const pathname = usePathname();
  const locale = useLocale();
  const router = useRouter();
  const { user } = useAuth();
  const player = useMediaPlayer();
  // coπ's voice (Settings → coπ voice): '' = the device's own.
  const layoutPrefs = useOptionalUserLayoutPrefs();
  const copiVoice = user ? layoutPrefs?.copiVoice || '' : '';
  const voiceRef = useRef(copiVoice);
  voiceRef.current = copiVoice;
  // Fetch "What did you mean?" in that voice ahead, so it plays at once.
  useEffect(() => { if (copiVoice) void copiVoiceUrl(copiVoice, 'whatDidYouMean', locale).catch(() => {}); }, [copiVoice, locale]);

  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  // Sitting over the chat button: placed from the BOTTOM like the button itself,
  // so it moves with it when a phone's address bar hides or shows on scroll
  // (Gordon 2026-11-03: "it drops over the chat button, then jumps back a second later").
  const [anchorBottom, setAnchorBottom] = useState<number | null>(null);
  const [listening, setListening] = useState(false);
  const [level, setLevel] = useState(0);
  const [bubble, setBubble] = useState('');
  // What coπ heard, shown above its answer — so "it didn't hear me" and
  // "it misunderstood me" look different (Gordon 2026-10-31).
  const [heard, setHeard] = useState('');
  // A one-tap follow-up offered in the bubble ("Use 440 · Standard instead").
  const [offer, setOffer] = useState<{ label: string; run: () => void; alt?: { label: string; run: () => void } } | null>(null);
  // "yes / ja / sí / oui" runs the offer on screen.
  const offerRef = useRef(offer);
  offerRef.current = offer;
  const [fullscreen, setFullscreen] = useState(false);
  // "What did you mean?" — coπ asks instead of guessing a search (Gordon
  // 2026-10-31: "it should talk back and ask … instead of opening search").
  const [ask, setAsk] = useState<{ heard: string; q: string } | null>(null);
  const askRef = useRef<{ heard: string; q: string } | null>(null);
  const [meant, setMeant] = useState('');
  // What coπ has learned (everyone's + your own), loaded once per visit:
  // taught phrases, remembered AI answers, name spellings (lib/copi-learning).
  const knowledgeRef = useRef<CopiKnowledge>({ pairs: new Map(), memory: new Map(), aliases: new Map() });
  useEffect(() => { void loadKnowledge(user).then(k => { knowledgeRef.current = k; }); }, [user]);

  // Which layer answered this command — counted once per command (aggregate
  // only) so Super Admin → coπ training shows how much the free layers carry.
  const turn = useRef({ reported: true, taught: false });
  const report = useCallback((layer: CopiLayer) => {
    const tr = turn.current;
    if (tr.reported) return;
    tr.reported = true;
    countCopiLayer(tr.taught && (layer === 'rules' || layer === 'catalogue') ? 'memory' : layer, locale);
  }, [locale]);

  // Self-learning (Gordon 2026-11-01: "can it train itself?"): an AI answer
  // that nobody undoes within COPI_CONFIRM_MS is remembered — for this person
  // at once, for everyone once 3 people got the same answer. Undoing it ("no",
  // "stop", back, asking again, "Not what you meant?") logs it for the nightly
  // review instead.
  const pendingLearn = useRef<{ text: string; action: CopiAiAction; timer: ReturnType<typeof setTimeout> } | null>(null);
  const learnCtx = useRef({ user, locale });
  learnCtx.current = { user, locale };
  const dropPending = useCallback((contradicted: boolean) => {
    const p = pendingLearn.current;
    if (!p) return false;
    clearTimeout(p.timer);
    pendingLearn.current = null;
    if (contradicted) logCopiMiss(p.text, 'contradicted', learnCtx.current.user, window.location.pathname, learnCtx.current.locale, JSON.stringify(p.action));
    return true;
  }, []);
  const learnLater = useCallback((text: string, action: CopiAiAction) => {
    dropPending(false);
    // A note holds the speaker's own words — never remembered.
    if (action.action === 'unknown' || action.action === 'note') return;
    const timer = setTimeout(() => {
      if (pendingLearn.current?.timer !== timer) return;
      pendingLearn.current = null;
      void rememberCopi(text, action, learnCtx.current.user, learnCtx.current.locale);
    }, COPI_CONFIRM_MS);
    pendingLearn.current = { text, action, timer };
  }, [dropPending]);
  useEffect(() => () => { if (pendingLearn.current) clearTimeout(pendingLearn.current.timer); }, []);

  const btnRef = useRef<HTMLButtonElement>(null);
  const drag = useRef<{ dx: number; dy: number; x0: number; y0: number; moved: boolean } | null>(null);
  const audio = useRef<{ stream: MediaStream; ctx: AudioContext; raf: number } | null>(null);
  const rec = useRef<any>(null);
  const wantListen = useRef(false);
  const bubbleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Every menu page, by its English name and its name in the page's language —
  // plus the creative apps, most of which live inside Studio, not the menu
  // (Gordon 2026-11-01: "coπ is a creative co-pilot for creative people").
  const pages = useMemo(() => {
    const list = NAV_PAGES.filter(p => user || !p.auth).map(p => {
      let local = '';
      if (p.navKey) { try { local = tNav(p.navKey); } catch { /* missing label */ } }
      return { href: p.href, names: [...p.names, local].filter(Boolean) };
    });
    for (const app of CREATIVE_APPS) {
      let local = '';
      if (app.navKey) { try { local = tNav(app.navKey); } catch { /* missing label */ } }
      const names = [...app.names, local].filter(Boolean);
      const had = list.find(p => p.href === app.href);
      if (had) had.names.push(...names.filter(n => !had.names.includes(n)));
      else list.push({ href: app.href, names });
    }
    // Browse pages that aren't menu items (Artists, Releases, Videos, People …)
    // and everyday names for menu pages — "show me news", "visa artister".
    for (const p of COPI_BROWSE_PAGES) {
      let local = '';
      const key = BROWSE_NAV_KEYS[p.href];
      if (key) { try { local = tNav(key); } catch { /* missing label */ } }
      list.push({ href: p.href, names: [...p.names, local].filter(Boolean) });
    }
    return list;
  }, [tNav, user]);

  const say = useCallback((text: string, ms = 3500) => {
    setBubble(text);
    if (bubbleTimer.current) clearTimeout(bubbleTimer.current);
    bubbleTimer.current = setTimeout(() => { setBubble(''); setHeard(''); setOffer(null); }, ms);
  }, []);

  /** coπ speaks: a fixed `line` in the chosen Gemini voice, else (or when the
   *  browser won't play it) the device's own voice — free. Only for questions. */
  const speak = useCallback((text: string, line?: CopiVoiceLine, voice = voiceRef.current) => {
    const device = () => {
      try {
        if (!('speechSynthesis' in window)) return;
        window.speechSynthesis.cancel();
        const u = new SpeechSynthesisUtterance(text);
        u.lang = speechLang(locale);
        window.speechSynthesis.speak(u);
      } catch { /* no voice on this device */ }
    };
    if (line && voice) void playCopiLine(voice, line, locale).catch(device);
    else device();
  }, [locale]);

  /** Couldn't place it: write the miss down and ASK — never a surprise search. */
  const askWhat = useCallback((heardText: string, q: string, reason: 'unplaced' | 'notFound' | 'wrong') => {
    report('unknown');
    // "Not what you meant?" right after an AI answer: that answer is undone —
    // logged once, as an AI answer to review, and never remembered.
    if (!(reason === 'wrong' && dropPending(true))) logCopiMiss(heardText, reason, user, pathname || '', locale);
    const a = { heard: heardText, q };
    askRef.current = a;
    setAsk(a);
    setMeant('');
    setBubble('');
    if (bubbleTimer.current) clearTimeout(bubbleTimer.current);
    bubbleTimer.current = setTimeout(() => { askRef.current = null; setAsk(null); setHeard(''); }, 45_000);
    if (reason !== 'wrong') speak(t('whatDidYouMean'), 'whatDidYouMean');
  }, [user, pathname, locale, speak, t, report, dropPending]);
  const closeAsk = useCallback(() => {
    askRef.current = null; setAsk(null); setHeard('');
    if (bubbleTimer.current) clearTimeout(bubbleTimer.current);
  }, []);

  // Same diameter as the chat button's width (Gordon 2026-10-31), measured
  // live so it follows the phone/desktop sizes.
  const sizeRef = useRef(DEFAULT_SIZE);
  const [size, setSize] = useState(DEFAULT_SIZE);

  // Position: a dragged spot is remembered on this device; until then it sits
  // right above the chat button and follows it (menu side, drag-swap, resize).
  const clamp = useCallback((x: number, y: number) => ({
    x: Math.max(4, Math.min(window.innerWidth - sizeRef.current - 4, x)),
    y: Math.max(4, Math.min(window.innerHeight - sizeRef.current - 4, y)),
  }), []);
  const anchored = useRef(true);
  const measureChat = useCallback(() => {
    const fab = document.querySelector('[data-bb-chat-fab]')?.getBoundingClientRect();
    if (fab?.width) {
      const w = Math.round(fab.width);
      if (w !== sizeRef.current) { sizeRef.current = w; setSize(w); }
    }
    return fab;
  }, []);
  const overChat = useCallback(() => {
    const fab = measureChat();
    const S = sizeRef.current;
    if (!fab || !fab.width) return { ...clamp(window.innerWidth - S - 16, window.innerHeight - S - 140), bottom: 140 };
    // Exactly centred over the chat button — no edge margin, it sits flush with the screen edge too.
    const x = Math.max(0, Math.min(window.innerWidth - S, fab.left + fab.width / 2 - S / 2));
    const y = Math.max(4, fab.top - S - 10);
    return { x, y, bottom: Math.round(window.innerHeight - y - S) };
  }, [clamp, measureChat]);
  useEffect(() => {
    // A dragged spot: { x, y } (upper half, from the top) or { x, bottom } (lower half,
    // from the bottom) — so a phone's address bar showing/hiding never moves it.
    let p: { x: number; y?: number; bottom?: number } | null = null;
    try { const raw = localStorage.getItem(POS_KEY); if (raw) p = JSON.parse(raw); } catch { /* storage blocked */ }
    anchored.current = !p;
    if (p && typeof p.bottom === 'number') {
      const b = Math.max(4, Math.min(window.innerHeight - sizeRef.current - 4, p.bottom));
      setPos(clamp(p.x, window.innerHeight - b - sizeRef.current));
      setAnchorBottom(b);
    } else if (p && typeof p.y === 'number') setPos(clamp(p.x, p.y));
    else { const o = overChat(); setPos({ x: o.x, y: o.y }); setAnchorBottom(o.bottom); }
    const follow = () => {
      if (!anchored.current) { measureChat(); return; }
      const next = overChat();
      setPos(cur => (cur && Math.abs(cur.x - next.x) < 1 && Math.abs(cur.y - next.y) < 1 ? cur : { x: next.x, y: next.y }));
      setAnchorBottom(cur => (cur !== null && Math.abs(cur - next.bottom) < 1 ? cur : next.bottom));
    };
    const timer = setInterval(follow, 1000);
    // Only a real width change re-fits a dragged spot; a height change is the
    // address bar, and the bottom/top anchoring already keeps the mic in place.
    let lastW = window.innerWidth;
    const onResize = () => {
      if (anchored.current) { follow(); return; }
      if (window.innerWidth === lastW) return;
      lastW = window.innerWidth;
      setPos(cur => (cur ? { ...cur, x: Math.max(4, Math.min(window.innerWidth - sizeRef.current - 4, cur.x)) } : cur));
    };
    // Hidden while presenting full screen — but not in EP-X focus mode, where coπ drives EP-X.
    const onFs = () => setFullscreen(!!document.fullscreenElement && !document.documentElement.hasAttribute('data-epx-focus'));
    window.addEventListener('resize', onResize);
    document.addEventListener('fullscreenchange', onFs);
    return () => { clearInterval(timer); window.removeEventListener('resize', onResize); document.removeEventListener('fullscreenchange', onFs); };
  }, [clamp, overChat, measureChat]);

  // Set below — words neither the rules nor the catalogue placed go to the AI.
  const understandRef = useRef<(text: string, fallbackQ?: string) => void>(() => {});

  /** "play <something>" — find it in the catalogue and start it. With the
   *  spoken words, a miss is handed to the AI before falling back to search. */
  const playMedia = useCallback(async (intent: MediaIntent, spoken?: string) => {
    say(t('looking'), 15_000);
    try {
      const lib = await loadCopiLibrary(user?.uid);
      const pick = pickMedia(intent, lib, { allowExplicit: !!user, avoidId: player.currentTrack?.id });
      // The rules understood it (an AI or memory answer has already been counted).
      if (pick.kind !== 'none' || pick.reason !== 'notFound') report('rules');
      if (pick.kind === 'tracks') {
        const tracks: MediaTrack[] = pick.tracks.map(tr => ({
          id: tr.id, title: tr.title, artist: tr.artist, imageUrl: tr.imageUrl, audioUrl: tr.audioUrl,
          mediaType: 'audio', genre: tr.genres[0],
        }));
        player.playTracks(tracks, 0);
        say(t('playingItem', { title: pick.label }), 4000);
      } else if (pick.kind === 'video') {
        const v = pick.video;
        player.playTrack({ id: v.id, title: v.title, artist: v.artist, imageUrl: v.imageUrl, audioUrl: '', youtubeId: v.youtubeId, mediaType: 'youtube' });
        window.dispatchEvent(new Event('open-player-sheet'));
        say(t('playingItem', { title: v.title }), 4000);
      } else if (pick.kind === 'playlist') {
        // Entries often hold only a reference — a work id or a whole release.
        const rows = await getPlaylistTracks(pick.playlist.id);
        const byId = new Map(lib.tracks.map(tr => [tr.id, tr]));
        const tracks: MediaTrack[] = rows.flatMap((r): MediaTrack[] => {
          if (r.audioUrl || r.youtubeId) {
            return [{ id: r.trackId || r.docId, title: r.title, artist: r.artist, imageUrl: r.imageUrl, audioUrl: r.audioUrl || '',
              youtubeId: r.youtubeId, mediaType: r.audioUrl ? 'audio' : 'youtube' }];
          }
          const rel = lib.releases.find(x => x.id === r.trackId);
          const found = rel ? rel.trackIds.map(id => byId.get(id)) : [byId.get(r.trackId)];
          return found.filter(Boolean).map(tr => ({ id: tr!.id, title: tr!.title, artist: tr!.artist, imageUrl: tr!.imageUrl, audioUrl: tr!.audioUrl, mediaType: 'audio' as const }));
        });
        if (!tracks.length) { say(t('nothingToPlay'), 4000); return; }
        player.playTracks(tracks, 0);
        say(t('playingItem', { title: pick.playlist.title }), 4000);
      } else if (pick.reason === 'noBpm') {
        say(t('noBpm', { bpm: intent.bpm ?? 0 }), 5000);
      } else if (pick.reason === 'notFound' && pick.q && spoken) {
        understandRef.current(spoken, pick.q);
      } else if (pick.reason === 'notFound' && pick.q) {
        askWhat(pick.q, pick.q, 'notFound');
      } else {
        say(t('nothingToPlay'), 4000);
      }
    } catch {
      say(t('nothingToPlay'), 4000);
    }
  }, [player, say, t, user, askWhat, report]);

  /** The page of what's playing: its artist, release or track. */
  const openCurrent = useCallback(async (what: 'artist' | 'release' | 'track' | 'download') => {
    const ct = player.currentTrack;
    if (what === 'download') {
      // The release's download window (or the track's, for a single work).
      if (!ct || ct.youtubeId || ct.mediaType === 'youtube') { say(t('nothingPlaying')); return; }
      let target = ct.id;
      try { target = (await loadCopiLibrary(user?.uid)).tracks.find(x => x.id === ct.id)?.releaseId || ct.id; } catch { /* the track */ }
      if (pathname === `/music/${target}`) window.dispatchEvent(new Event('bb-open-download'));
      else router.push(`/music/${target}?download=1`);
      say(t('opening', { page: ct.title }));
      return;
    }
    if (!ct) {
      if (what === 'artist') { router.push('/artists'); say(t('opening', { page: tNav('artists') })); }
      else say(t('nothingPlaying'));
      return;
    }
    if (ct.youtubeId || ct.mediaType === 'youtube') { router.push(`/videos/${ct.id}`); say(t('opening', { page: ct.title })); return; }
    let href = `/music/${ct.id}`, name = ct.title;
    try {
      const lib = await loadCopiLibrary(user?.uid);
      if (what === 'artist' && ct.artist) {
        const hit = findEntity(ct.artist, lib, 'artist', 0.8);
        if (hit) { href = hit.href; name = hit.name; } else { href = `/search?q=${encodeURIComponent(ct.artist)}`; name = ct.artist; }
      } else if (what === 'release') {
        const rid = lib.tracks.find(x => x.id === ct.id)?.releaseId;
        const rel = rid ? lib.releases.find(r => r.id === rid) : undefined;
        if (rel) { href = `/music/${rel.id}`; name = rel.title; }
      }
    } catch { /* the track's own page */ }
    router.push(href);
    say(t('opening', { page: name }));
  }, [player, router, say, t, tNav, user, pathname]);

  /** The tuner: open it listening, at a named tuning or an exact A4. An
   *  unusual number is set exactly, with the nearest classic one offered. */
  const openTuner = useCallback((a: { a4?: number; preset?: string }) => {
    let a4: number | undefined;
    let nearest: TuningPreset | undefined;
    if (a.preset) a4 = TUNINGS.find(p => p.id === a.preset)?.a4;
    else if (a.a4 !== undefined) {
      if (a.a4 < A4_MIN || a.a4 > A4_MAX) { say(t('tunerRange', { min: A4_MIN, max: A4_MAX }), 5000); return; }
      const near = nearestTuning(a.a4);
      if (Math.abs(near.a4 - a.a4) < 0.05) a4 = near.a4;
      else { a4 = a.a4; if (Math.abs(near.a4 - a.a4) <= 4) nearest = near; }
    }
    if (pathname?.startsWith('/studio/tuner')) window.dispatchEvent(new CustomEvent('bb-tuner', { detail: { a4, listen: true } }));
    else router.push(`/studio/tuner?listen=1${a4 ? `&a4=${a4}` : ''}`);
    say(a4 ? t('tunerSet', { hz: a4 }) : t('tunerOpening'), nearest ? 10_000 : 3500);
    if (nearest) {
      const n = nearest;
      setOffer({
        label: t('tunerNearest', { hz: n.a4, name: tTuner(`presets.${n.id}`) }),
        run: () => { window.dispatchEvent(new CustomEvent('bb-tuner', { detail: { a4: n.a4 } })); say(t('tunerSet', { hz: n.a4 })); },
      });
    }
  }, [pathname, router, say, t, tTuner]);

  /** Signed out: these need an account — off to sign in, then back to `to`. */
  const needSignIn = useCallback((to: string) => {
    router.push(`/signin?redirect=${encodeURIComponent(to)}`);
    say(t('signInFirst'), 5000);
  }, [router, say, t]);

  /**
   * A Discogs link → the BB release it is → its credits window with the
   * Discogs credits ready to review (Gordon 2026-11-03: "drop a Discogs link
   * in coπ"). Creators and admins; the import itself is always reviewed.
   */
  const importFromDiscogs = useCallback(async (ref: string) => {
    if (!user) { needSignIn(pathname || '/home'); return; }
    say(t('discogsReading'), 12_000);
    try {
      const res = await fetchWithAuth('/api/discogs/match', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ref }) });
      const d = await res.json().catch(() => ({}));
      if (res.status === 403) { say(t('discogsNotAllowed'), 6000); return; }
      if (!res.ok || !d.discogs) { say(d.error === 'bad-ref' ? t('discogsNoLink') : t('discogsFailed'), 6000); return; }
      const name = `${d.discogs.artist} – ${d.discogs.title}`;
      const open = (r: { id: string; title: string }) => {
        router.push(`/media?credits=${encodeURIComponent(r.id)}&discogs=${d.discogs.id}`);
        say(t('discogsOpening', { title: r.title }), 5000);
      };
      const list: { id: string; title: string; artist: string; score: number }[] = d.releases || [];
      if (!list.length) { say(t('discogsNoRelease', { title: name }), 7000); return; }
      if (list.length === 1 || list[0].score > list[1].score) { open(list[0]); return; }
      say(t('discogsWhich', { title: name }), 15_000);
      setOffer({
        label: `${list[0].title}${list[0].artist ? ` – ${list[0].artist}` : ''}`,
        run: () => open(list[0]),
        alt: { label: `${list[1].title}${list[1].artist ? ` – ${list[1].artist}` : ''}`, run: () => open(list[1]) },
      });
    } catch { say(t('discogsFailed'), 6000); }
  }, [user, needSignIn, pathname, say, t, router]);

  /** "song idea: …" → a new song in Studio → Songwriting (Gordon 2026-11-01). */
  const saveNote = useCallback(async (a: { kind: 'idea' | 'lyrics' | 'concept'; text: string }) => {
    if (!user) { needSignIn('/studio/songwriter'); return; }
    if (!a.text) { router.push('/studio/songwriter'); say(t('noteHowTo'), 7000); return; }
    say(t('looking'), 15_000);
    try {
      const title = a.text.split(/\s+/).slice(0, 6).join(' ').slice(0, 60);
      const song = await createSong(user.uid, title, a.kind === 'lyrics' ? { lyrics: a.text } : { notes: a.text });
      say(t(a.kind === 'lyrics' ? 'lyricsSaved' : 'noteSaved'), 8000);
      setOffer({ label: t('noteOpen'), run: () => router.push(`/studio/songwriter?id=${song.id}`) });
    } catch {
      say(t('noteFailed'), 5000);
    }
  }, [user, needSignIn, router, say, t]);

  /** "change voice" → the picker; "random / male / female / phone voice", or a
   *  name → switched at once, and coπ says hello in it. */
  const changeVoice = useCallback(async (a: { gender?: 'female' | 'male' | 'neutral'; random?: boolean; device?: boolean; id?: string }) => {
    if (!user || !layoutPrefs) { needSignIn('/settings/voice'); return; }
    if (a.device) {
      await layoutPrefs.updateCopiVoice('');
      say(t('voiceDevice'), 5000);
      speak(t('voicePreview'), undefined, '');
      return;
    }
    let pick = a.id ? MAIN_VOICES.find(v => v.id === a.id) : undefined;
    if (!pick && (a.gender || a.random)) {
      const pool = MAIN_VOICES.filter(v => (!a.gender || v.gender === a.gender) && v.id !== voiceRef.current);
      pick = pool[Math.floor(Math.random() * pool.length)];
    }
    if (!pick) { router.push('/settings/voice'); say(t('opening', { page: t('voicePage') })); return; }
    await layoutPrefs.updateCopiVoice(pick.id);
    say(t('voiceChanged', { name: pick.name }), 6000);
    setOffer({ label: t('voiceMore'), run: () => router.push('/settings/voice') });
    speak(t('voicePreview'), 'voicePreview', pick.id);
  }, [user, layoutPrefs, needSignIn, router, say, t, speak]);

  /** "make an electro beat in EPX at 118 bpm": open Studio → EP-X, ask EP-X what
   *  it can do, let the AI plan a few of those commands, run them one by one —
   *  EP-X checks every value and reads its state back (Gordon 2026-11-01). */
  const runEpx = useCallback(async (prompt: string) => {
    say(t('epxWorking'), 30_000);
    if (!epxMounted()) router.push('/studio/epx');
    for (let i = 0; i < 60 && !epxMounted(); i++) await new Promise(r => setTimeout(r, 150));
    const commands = (await epxReady(25_000)) ? await epxCommandList() : null;
    if (!commands) { say(t('epxNotReady'), 7000); return; }
    const state = await epxCommand('state.get');
    let plan: { steps?: { command: string; params?: Record<string, unknown> }[]; reply?: string; unsupported?: string; tokensLeft?: number; error?: string; required?: number; balance?: number } | null = null;
    let status = 0;
    try {
      const res = await fetchWithAuth('/api/ai/copi/epx', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ said: prompt, commands, locale, state: state.ok ? state.result : undefined }),
      });
      status = res.status;
      plan = await res.json();
    } catch { /* offline */ }
    if (status === 401) { needSignIn('/studio/epx'); return; }
    if (status === 402) { say(t('askNoTokens', { cost: plan?.required ?? 1, balance: plan?.balance ?? 0 }), 9000); return; }
    if (!plan?.steps?.length) { say(plan?.unsupported || t('epxCouldNot'), 8000); return; }
    const steps = plan.steps;
    const runSteps = async () => {
      say(t('epxWorking'), 90_000);
      let silent = false;
      for (const step of steps) {
        const r = await epxCommand(step.command, step.params);
        if ((r as { audio?: string }).audio === 'suspended') silent = true;
        if (!r.ok) { say(r.code === 'needs_gesture' ? t('epxTapForSound') : t('epxStepFailed', { error: r.error || step.command }), 9000); return; }
      }
      const spent = typeof plan?.tokensLeft === 'number' ? t('tokensSpent', { cost: 1, left: plan.tokensLeft }) : '';
      say([plan?.reply || t('epxDone'), plan?.unsupported, silent ? t('epxTapForSound') : '', spent].filter(Boolean).join(' '), 10_000);
    };
    // Loading a beat or a kit replaces what's on the pads — ask first when there is something.
    const replaces = steps.some(st => ['beat.make', 'demo.load', 'project.new', 'project.starter', 'kit.load'].includes(st.command));
    const pads = state.ok ? ((state.result as { pads?: unknown[] })?.pads?.length ?? 0) : 0;
    if (replaces && pads > 0) {
      say(t('epxReplaceAsk'), 45_000);
      setOffer({ label: t('epxReplace'), run: () => void runSteps(), alt: { label: tCommon('cancel'), run: () => say(t('closed'), 2000) } });
      return;
    }
    await runSteps();
  }, [router, say, t, tCommon, locale, needSignIn]);

  /** Read a long text aloud with the device's voice, a sentence or two at a time
   *  (Chrome cuts off long utterances). "stop" stops it. */
  const readAloud = useCallback((text: string, lang: string) => {
    try {
      const synth = window.speechSynthesis;
      if (!synth) return false;
      synth.cancel();
      const parts: string[] = [];
      for (const piece of text.replace(/\s*\n+\s*/g, '\n').split(/(?<=[.!?…])\s+|\n/)) {
        const p = piece.trim();
        if (!p) continue;
        const last = parts[parts.length - 1];
        if (last && last.length + p.length < 200) parts[parts.length - 1] = `${last} ${p}`;
        else parts.push(p);
      }
      for (const p of parts) { const u = new SpeechSynthesisUtterance(p); u.lang = lang; synth.speak(u); }
      return parts.length > 0;
    } catch { return false; }
  }, []);
  const hush = useCallback(() => {
    try { window.speechSynthesis?.cancel(); } catch { /* none */ }
    try { copiVoiceElement().pause(); } catch { /* none */ }
  }, []);

  // What "read it" means right now: the lesson coπ just opened, else the newest chat.
  const readCtx = useRef<{ kind: 'lesson'; at: number } | null>(null);
  const lastMsg = useRef<ChatMessage | null>(null);

  /** The lesson on screen, as shown (its translation once it has arrived). */
  const readLesson = useCallback(() => {
    readCtx.current = null;
    const els = Array.from(document.querySelectorAll<HTMLElement>('[data-lesson-read]'));
    if (!els.length) { say(t('lessonNotOpen'), 5000); return; }
    const lang = els[0].closest('[lang]')?.getAttribute('lang') || locale;
    if (readAloud(els.map(e => e.innerText).join('\n'), speechLang(lang))) say(t('readingLesson'), 6000);
  }, [readAloud, say, t, locale]);

  /** "teach me the circle of fifths" → that Academy lesson, then "read it to you?" */
  const openLesson = useCallback(async (topic: string) => {
    say(t('looking'), 10_000);
    const { findLesson } = await import('@/lib/academy/find-lesson');
    const hit = findLesson(topic);
    if (!hit) { router.push('/academy'); say(t('lessonNotFound', { topic }), 7000); return; }
    router.push(`/academy/${hit.courseId}?lesson=${encodeURIComponent(hit.lessonId)}`);
    window.dispatchEvent(new CustomEvent('bb-academy-lesson', { detail: hit }));
    readCtx.current = { kind: 'lesson', at: Date.now() };
    say(t('lessonAsk', { title: hit.title }), 60_000);
    setOffer({ label: t('readToMe'), run: readLesson, alt: { label: t('readMyself'), run: () => { readCtx.current = null; say(t('enjoyReading'), 3000); } } });
    speak(t('readOrSelf'), 'readOrSelf');
  }, [router, say, t, readLesson, speak]);

  /** "give me a production tip" → a random Academy takeaway they haven't heard. */
  const giveTip = useCallback(async (kind: 'production' | 'theory' | 'business' | 'any') => {
    const { pickTip } = await import('@/lib/academy/find-lesson');
    let heard: string[] = [];
    try { heard = JSON.parse(localStorage.getItem('bb-copi-tips') || '[]'); } catch { /* storage blocked */ }
    const tip = pickTip(kind, new Set(heard));
    if (!tip) { say(t('noTips'), 4000); return; }
    try { localStorage.setItem('bb-copi-tips', JSON.stringify([...heard.filter(k => k !== tip.key), tip.key].slice(-400))); } catch { /* storage blocked */ }
    let text = tip.text;
    let lesson = tip.lessonTitle;
    if (locale !== 'en') {
      try {
        const tr = await fetch(`/api/academy/translate?locale=${encodeURIComponent(locale)}&lesson=${encodeURIComponent(tip.lessonId)}`).then(r => (r.ok ? r.json() : null));
        if (typeof tr?.keyTakeaways?.[tip.index] === 'string') text = tr.keyTakeaways[tip.index];
        if (typeof tr?.title === 'string') lesson = tr.title;
      } catch { /* English then */ }
    }
    setHeard('');
    say(`${text} — ${t('tipFrom', { lesson })}`, 20_000);
    setOffer({
      label: t('anotherTip'), run: () => void giveTipRef.current(kind),
      alt: { label: t('openLesson'), run: () => router.push(`/academy/${tip.courseId}?lesson=${encodeURIComponent(tip.lessonId)}`) },
    });
    readAloud(text, speechLang(locale === 'en' || text !== tip.text ? locale : 'en'));
  }, [locale, router, say, t, readAloud]);
  const giveTipRef = useRef(giveTip);
  giveTipRef.current = giveTip;

  /** A question → the Academy tutor's music brain (2 tokens, like the tutor), read aloud. */
  const askTutor = useCallback(async (question: string) => {
    if (!user) { needSignIn(pathname || '/academy'); return; }
    say(t('thinking'), 30_000);
    try {
      const res = await fetchWithAuth('/api/ai/creator-advisor', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: 'copi', question }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 402) { say(t('askNoTokens', { cost: data?.required ?? 2, balance: data?.balance ?? 0 }), 9000); return; }
      const answer = String(data?.answer || '').replace(/[#*_`>]/g, '').trim();
      if (!res.ok || !answer) { say(t('askFailed'), 6000); return; }
      const spent = typeof data?.tokensLeft === 'number' ? `\n${t('tokensSpent', { cost: data.tokensSpent ?? 2, left: data.tokensLeft })}` : '';
      say(answer + spent, Math.min(60_000, 8000 + answer.length * 60));
      readAloud(answer, speechLang(locale));
      const { findLesson } = await import('@/lib/academy/find-lesson');
      const hit = findLesson(question);
      if (hit) setOffer({ label: t('openLesson'), run: () => router.push(`/academy/${hit.courseId}?lesson=${encodeURIComponent(hit.lessonId)}`) });
    } catch {
      say(t('askFailed'), 6000);
    }
  }, [user, needSignIn, pathname, say, t, readAloud, locale, router]);

  /** "read it" for a chat: the newest message, who sent it, and what it carries. */
  const readChat = useCallback(async () => {
    if (!user) { needSignIn('/home'); return; }
    const roomId = (window as any).__bbLastChatRoom as string | null;
    const m = roomId ? await getLatestMessage(roomId).catch(() => null) : null;
    if (!m) { say(t('noNewMessages'), 4000); return; }
    lastMsg.current = m;
    const att = m.type === 'shared_content' && m.sharedContent ? m.sharedContent.title
      : m.mediaData?.type === 'image' || m.type === 'image' ? t('attImage')
      : m.mediaData?.type === 'video' || m.type === 'video' ? t('attVideo')
      : m.mediaData?.type === 'audio' || m.type === 'audio' ? t('attAudio') : '';
    const body = (m.text || '').trim();
    setHeard('');
    say(`${m.senderName || ''}: ${body || att}`, 20_000);
    readAloud([t('messageFrom', { name: m.senderName || '' }), body, att ? t('withAttachment', { what: att }) : ''].filter(Boolean).join('. '), speechLang(locale));
    setOffer(att
      ? { label: t('openAttachment'), run: () => openAttachmentRef.current(), alt: { label: t('openChat'), run: () => window.dispatchEvent(new CustomEvent('openChatRoom', { detail: { roomId: m.roomId } })) } }
      : { label: t('openChat'), run: () => window.dispatchEvent(new CustomEvent('openChatRoom', { detail: { roomId: m.roomId } })) });
  }, [user, needSignIn, say, t, readAloud, locale]);

  /** "open the attachment": a shared track/link opens its page; pictures, videos and voice messages open in the chat. */
  const openAttachment = useCallback(() => {
    const m = lastMsg.current;
    const roomId = m?.roomId || ((window as any).__bbLastChatRoom as string | null);
    const link = m?.sharedContent?.link;
    if (link) {
      if (link.startsWith('/')) router.push(link); else window.open(link, '_blank', 'noopener,noreferrer');
      say(t('opening', { page: m!.sharedContent!.title }));
      return;
    }
    if (!roomId) { say(t('noNewMessages'), 4000); return; }
    window.dispatchEvent(new CustomEvent('openChatRoom', { detail: { roomId } }));
    say(t('openingChat'));
  }, [router, say, t]);
  const openAttachmentRef = useRef(openAttachment);
  openAttachmentRef.current = openAttachment;

  // A new chat message while BB is open: coπ says it (Settings → chat sounds →
  // "Announce new messages"), then "read it" reads it (Gordon 2026-11-01).
  useEffect(() => {
    const onNew = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      // A booking request is always shown (Gordon 2026-11-03: "a notification from coπ too"); spoken when announcing is on.
      if (d.kind === 'booking' && typeof d.link === 'string' && d.link.startsWith('/')) {
        const line = t('newBooking', { name: d.senderName || '' });
        if (d.voice) speak(line);
        setHeard('');
        say(`${line}${d.preview ? ` — ${d.preview}` : ''}`, 30_000);
        setOffer({ label: t('openBooking'), run: () => router.push(d.link), alt: { label: t('openChat'), run: () => window.dispatchEvent(new CustomEvent('openChatRoom', { detail: { roomId: d.roomId } })) } });
        return;
      }
      if (!d.voice) return;
      readCtx.current = null;
      lastMsg.current = null;
      if (voiceRef.current) speak(t('newMessage'), 'newMessage');
      else speak(t('newMessageFrom', { name: d.senderName || '' }));
      setHeard('');
      say(`${t('newMessageFrom', { name: d.senderName || '' })}${d.preview ? ` — ${d.preview}` : ''}`, 20_000);
      setOffer({ label: t('readIt'), run: () => void readChat() });
    };
    window.addEventListener('bb-chat-new', onNew);
    return () => window.removeEventListener('bb-chat-new', onNew);
  }, [speak, say, t, readChat, router]);

  /** Tokens are spent only after the person knows (Gordon 2026-11-01): the first
   *  time for each kind of action coπ says the cost and waits for "yes" or a tap;
   *  "yes, don't ask again" remembers it on this device. */
  const confirmSpend = useCallback((kind: string, cost: number, go: () => void) => {
    let ok: string[] = [];
    try { ok = JSON.parse(localStorage.getItem('bb-copi-spend-ok') || '[]'); } catch { /* storage blocked */ }
    if (ok.includes(kind)) { go(); return; }
    setHeard('');
    say(t('costAsk', { cost }), 45_000);
    setOffer({
      label: t('costYes', { cost }), run: go,
      alt: { label: t('costAlways'), run: () => { try { localStorage.setItem('bb-copi-spend-ok', JSON.stringify([...ok, kind])); } catch { /* storage blocked */ } go(); } },
    });
  }, [say, t]);

  /** "how many tokens", "my last purchase", "my last messages". */
  const answerAccount = useCallback(async (what: 'tokens' | 'lastPurchase' | 'messages') => {
    if (!user) { needSignIn(what === 'messages' ? '/home' : '/tokens'); return; }
    if (what === 'messages') {
      const rooms = ((window as any).__bbChatRooms || []) as { id: string; groupName: string; names: Record<string, string>; lastSenderId: string; lastSenderName: string; lastText: string }[];
      const fromOthers = rooms.filter(r => r.lastSenderId && r.lastSenderId !== user.uid && r.lastText).slice(0, 3);
      if (!fromOthers.length) { say(t('noNewMessages'), 4000); return; }
      const lines = fromOthers.map(r => `${r.lastSenderName || r.groupName}: ${r.lastText}`);
      setHeard('');
      say(lines.join(' · '), 20_000);
      readAloud(lines.join('. '), speechLang(locale));
      setOffer({ label: t('openChat'), run: () => window.dispatchEvent(new CustomEvent('openChatRoom', { detail: { roomId: fromOthers[0].id } })) });
      return;
    }
    say(t('looking'), 10_000);
    const res = await fetchWithAuth('/api/copi/account').catch(() => null);
    const d = res && res.ok ? await res.json().catch(() => null) : null;
    if (!d) { say(t('askFailed'), 5000); return; }
    setHeard('');
    if (what === 'tokens') {
      const last = (d.spends || [])[0];
      const text = t('tokensBalance', { balance: d.balance ?? 0 }) + (last ? ` ${t('tokensLastSpend', { amount: Math.abs(last.amount), reason: last.reason })}` : '');
      say(text, 12_000);
      speak(t('tokensBalance', { balance: d.balance ?? 0 }));
      setOffer({ label: t('tokensTopUp'), run: () => router.push('/tokens'), alt: { label: t('tokensHistory'), run: () => router.push('/tokens#history') } });
    } else {
      const p = d.lastPurchase;
      if (!p) { say(t('noPurchases'), 5000); return; }
      const when = p.at ? new Date(p.at).toLocaleDateString(locale) : '';
      const price = p.amount ? new Intl.NumberFormat(locale, { style: 'currency', currency: p.currency || 'EUR' }).format(p.amount) : '';
      const text = t('lastPurchaseWas', { title: p.title || '—', price: price || '—', date: when || '—' });
      say(text, 12_000);
      speak(text);
    }
  }, [user, needSignIn, say, t, readAloud, locale, speak, router]);

  /** "write a message to Ski saying what's up (and send it)". The person is found
   *  in their own chats; with "send it" it goes after a 4-second countdown they
   *  can stop, otherwise it waits for "Send". */
  const sendTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftMessage = useCallback((to: string, text: string, sendNow: boolean) => {
    if (!user) { needSignIn('/home'); return; }
    const key = (v: string) => v.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^\p{L}\p{N} ]/gu, '').trim();
    const want = key(to);
    const rooms = ((window as any).__bbChatRooms || []) as { id: string; type: string; groupName: string; names: Record<string, string> }[];
    const hits = rooms.flatMap(r => {
      const other = Object.entries(r.names || {}).filter(([uid]) => uid !== user.uid).map(([, n]) => n);
      const label = r.type === 'group' ? r.groupName : other[0] || '';
      const words = [label, ...other].map(key).join(' ').split(' ');
      return want && (key(label) === want || want.split(' ').every(w => words.includes(w))) ? [{ id: r.id, label }] : [];
    });
    if (!hits.length) {
      say(t('sendNoMatch', { name: to }), 8000);
      setOffer({ label: t('openChat'), run: () => window.dispatchEvent(new CustomEvent('openChatRoom', { detail: {} })) });
      return;
    }
    const doSend = async (roomId: string, label: string) => {
      if (sendTimer.current) { clearTimeout(sendTimer.current); sendTimer.current = null; }
      try {
        await sendMessage(roomId, user.uid, user.displayName || '', text, user.photoURL || undefined);
        say(t('messageSent', { name: label }), 5000);
      } catch { say(t('messageFailed'), 6000); }
    };
    setHeard('');
    if (hits.length > 1) {
      // Two people match — they pick.
      say(t('sendWhich', { name: to, text }), 30_000);
      setOffer({ label: hits[0].label, run: () => void doSend(hits[0].id, hits[0].label), alt: { label: hits[1].label, run: () => void doSend(hits[1].id, hits[1].label) } });
      return;
    }
    const hit = hits[0];
    if (sendNow) {
      say(t('sendingIn', { name: hit.label, text }), 6000);
      sendTimer.current = setTimeout(() => void doSend(hit.id, hit.label), 4000);
      setOffer({ label: tCommon('cancel'), run: () => { if (sendTimer.current) clearTimeout(sendTimer.current); sendTimer.current = null; say(t('closed'), 2000); } });
    } else {
      say(t('sendDraft', { name: hit.label, text }), 30_000);
      setOffer({ label: t('send'), run: () => void doSend(hit.id, hit.label), alt: { label: tCommon('cancel'), run: () => say(t('closed'), 2000) } });
    }
  }, [user, needSignIn, say, t, tCommon]);

  /** "show me <name>" (Gordon 2026-11-02: "show me Ski" → Ski's profile):
   *  a style opens its releases (or artists), a name opens its page — never
   *  plays. Not found → the usual memory → AI → ask. */
  const showPlace = useCallback(async (q: string, kind?: ShowKind) => {
    say(t('looking'), 15_000);
    const spelled = applyCopiAliases(q, knowledgeRef.current.aliases);
    let lib: CopiLibrary | null = null;
    let places: CopiPlace[] = [];
    try { [lib, places] = await Promise.all([loadCopiLibrary(user?.uid), loadCopiPlaces()]); } catch { /* ask below */ }
    if (lib) {
      const style = !kind || kind === 'artist' || kind === 'release' || kind === 'track' ? styleNamed(spelled, lib) : null;
      if (style) {
        report('catalogue');
        router.push(`${kind === 'artist' ? '/artists' : '/releases'}?genre=${encodeURIComponent(style)}`);
        setTimeout(() => window.dispatchEvent(new Event('bb-url-params')), 80);
        say(t('opening', { page: style }));
        return;
      }
      const hit = findPlace(spelled, lib, places, kind);
      if (hit) { report('catalogue'); router.push(hit.href); say(t('opening', { page: hit.name })); return; }
    }
    understandRef.current(`show ${q}`);
  }, [say, t, user, router, report]);

  const run = useCallback((action: CopiAction) => {
    // Doing something in BB from EP-X's full screen ("play Desmond Foster") leaves it.
    if (typeof document !== 'undefined' && document.documentElement.hasAttribute('data-epx-focus')
      && !['epx', 'stop', 'yes', 'pause', 'play', 'next', 'prev', 'restart', 'read', 'selfRead', 'account', 'tip', 'ask', 'sendMessage', 'memo'].includes(action.type)) {
      window.dispatchEvent(new Event('bb-epx-exit'));
    }
    switch (action.type) {
      case 'yes': {
        const o = offerRef.current;
        if (o) { setOffer(null); o.run(); } else say(t('nothingToConfirm'), 3000);
        break;
      }
      case 'account': void answerAccount(action.what); break;
      case 'sendMessage': draftMessage(action.to, action.text, action.send); break;
      case 'learn': void openLesson(action.topic); break;
      case 'read':
        if (readCtx.current && Date.now() - readCtx.current.at < 15 * 60_000) readLesson();
        else void readChat();
        break;
      case 'selfRead': readCtx.current = null; hush(); say(t('enjoyReading'), 3000); break;
      case 'openAttachment': openAttachment(); break;
      case 'tip': void giveTip(action.kind); break;
      case 'create':
        if (!user) { needSignIn(action.what === 'cover' ? '/artwork-studio' : '/film-maker'); break; }
        if (action.what === 'cover') {
          // Artwork Studio turns the words into a cover brief + guide (1 token); generating images stays their tap.
          const brief = action.brief;
          confirmSpend('cover', 1, () => {
            if (pathname?.startsWith('/artwork-studio')) window.dispatchEvent(new CustomEvent('bb-artwork-brief', { detail: { brief } }));
            else router.push(`/artwork-studio?brief=${encodeURIComponent(brief)}`);
            say(t('coverOpening'), 6000);
          });
        } else {
          router.push('/film-maker');
          say(t('filmOpening'), 6000);
        }
        break;
      case 'memo':
        if (!user) { needSignIn('/studio/recorder?ready=1'); break; }
        if (pathname?.startsWith('/studio/recorder')) window.dispatchEvent(new Event('bb-memo-ready'));
        else router.push('/studio/recorder?ready=1');
        say(t('memoReady'), 6000);
        break;
      case 'ask': if (!user) { needSignIn(pathname || '/academy'); break; } confirmSpend('ask', 2, () => void askTutor(action.question)); break;
      case 'epx': if (!user) { needSignIn('/studio/epx'); break; } confirmSpend('epx', 1, () => void runEpx(action.prompt)); break;
      case 'snap':
        if (!user) { needSignIn('/bookkeeping'); break; }
        // The bookkeeping camera opens ready to shoot; its listener answers with
        // preventDefault(). Nobody answered → Bookkeeping, which has a scanner.
        if (window.dispatchEvent(new CustomEvent('bb-snap', { detail: { kind: action.kind }, cancelable: true }))) {
          router.push('/bookkeeping');
          say(t('opening', { page: tNav('bookkeeping') }));
        } else say(t('snapOpening'));
        break;
      case 'note': void saveNote(action); break;
      case 'discogs': {
        if (action.ref) { void importFromDiscogs(action.ref); break; }
        // Said, not pasted: take the link from the clipboard, or say how.
        void (async () => {
          let text = '';
          try { text = await navigator.clipboard.readText(); } catch { /* not allowed here */ }
          const link = DISCOGS_LINK.exec(text || '')?.[0];
          if (link) void importFromDiscogs(link); else say(t('discogsHowTo'), 8000);
        })();
        break;
      }
      case 'copilot': {
        if (!user) { needSignIn(pathname || '/home'); break; }
        // "this track" means what's playing — say which, so Copilot knows.
        const ct = player.currentTrack;
        const deictic = /\b(this|den här|det här|denna|detta|esta|este|esto|ce|cet|cette|ça)\b/i.test(action.prompt);
        const prompt = ct && deictic ? `${action.prompt} (${t('nowPlayingNote', { title: ct.title })})` : action.prompt;
        // Copilot's listener answers with preventDefault(); else its page.
        if (window.dispatchEvent(new CustomEvent('bb-copilot', { detail: { prompt }, cancelable: true }))) router.push('/creator-ai');
        say(t('copilotOpening'));
        break;
      }
      case 'voice': void changeVoice(action); break;
      case 'tuner': openTuner(action); break;
      case 'navigate':
        router.push(action.href);
        // Same page, new filter ("show me hoodies" on the shop) — tell it to re-read the address.
        if (action.href.includes('?')) setTimeout(() => window.dispatchEvent(new Event('bb-url-params')), 80);
        say(t('opening', { page: action.name }));
        break;
      case 'show': void showPlace(action.q, action.kind); break;
      case 'back': router.back(); say(t('goingBack')); break;
      // Nothing loaded yet → something to listen to instead of silence.
      case 'play': if (player.currentTrack) { player.play(); say(t('playing')); } else void playMedia({ kind: 'any', random: true }); break;
      case 'media': { const { type: _type, ...intent } = action; void playMedia(intent); break; }
      // "stop" while coπ reads aloud stops the reading, not the music.
      case 'pause': if (window.speechSynthesis?.speaking) { hush(); say(t('paused')); break; } player.pause(); say(t('paused')); break;
      case 'next': player.skipNext(); say(t('nextTrack')); break;
      case 'prev': player.skipPrev(); say(t('previousTrack')); break;
      case 'restart': player.seek(0); player.play(); say(t('playing')); break;
      case 'current': void openCurrent(action.what); break;
      case 'close': {
        // The search window closes like its header X: back, or Home.
        if (action.what !== 'player' && pathname?.startsWith('/search')) {
          if (window.history.length > 1) router.back(); else router.push('/home');
        } else {
          window.dispatchEvent(new Event('close-player-sheet'));
        }
        say(t('closed'));
        break;
      }
      case 'search': router.push(`/search?q=${encodeURIComponent(action.q)}`); say(t('searching', { q: action.q })); break;
      case 'chat': window.dispatchEvent(new CustomEvent('openChatRoom', { detail: {} })); say(t('openingChat')); break;
      case 'favorites': window.dispatchEvent(new Event('bb-toggle-favorites')); say(t('favorites')); break;
      case 'crates': window.dispatchEvent(new CustomEvent('bb-crates', { detail: { open: true } })); say(t('crates')); break;
      case 'lowVision': window.dispatchEvent(new CustomEvent('bb-low-vision', { detail: { on: action.on } })); say(action.on ? t('lowVisionOn') : t('lowVisionOff')); break;
      case 'player': window.dispatchEvent(new Event('open-player-sheet')); say(t('playerOpened')); break;
      case 'menu': window.dispatchEvent(new CustomEvent('bb-menu', { detail: { open: action.open } })); say(action.open === false ? t('menuClosed') : t('menuOpened')); break;
      case 'hero': window.dispatchEvent(new CustomEvent('bb-hero', { detail: { closed: !action.show } })); say(action.show ? t('heroShown') : t('heroClosed')); break;
      case 'stop': hush(); stopRef.current(); break;
    }
  }, [answerAccount, draftMessage, confirmSpend, router, player, say, t, tNav, playMedia, openCurrent, pathname, openTuner, user, needSignIn, saveNote, changeVoice, runEpx, openLesson, readLesson, readChat, hush, openAttachment, giveTip, askTutor]);

  /** The AI's answer → the same actions the rules produce. `said`: the words, for Copilot. */
  const applyAi = useCallback((ai: CopiAiAction, lib: CopiLibrary, said = ''): boolean => {
    switch (ai.action) {
      case 'navigate': {
        const page = pages.find(p => p.href === ai.href);
        if (!page) return false;
        run({ type: 'navigate', href: page.href, name: page.names[page.names.length - 1] || page.href });
        return true;
      }
      case 'media': { const { action: _a, ...intent } = ai; void playMedia(intent); return true; }
      case 'open': {
        const hit = findEntity(ai.name, lib, ai.kind, 0.6);
        if (!hit) return false;
        router.push(hit.href); say(t('opening', { page: hit.name }));
        return true;
      }
      case 'search': run({ type: 'search', q: ai.q }); return true;
      case 'close': run({ type: 'close', what: 'any' }); return true;
      case 'menu': run({ type: 'menu', open: ai.open }); return true;
      case 'play': case 'pause': case 'next': case 'prev': case 'restart': case 'back': case 'chat': case 'favorites':
        run({ type: ai.action }); return true;
      case 'snap': run({ type: 'snap', kind: ai.kind }); return true;
      case 'note': run({ type: 'note', kind: ai.kind, text: ai.text }); return true;
      case 'copilot': if (!said) return false; run({ type: 'copilot', prompt: said }); return true;
      case 'epx': if (!said) return false; run({ type: 'epx', prompt: said }); return true;
      case 'voice': { const { action: _a, ...v } = ai; run({ type: 'voice', ...v }); return true; }
    }
    return false; // 'unknown' — ask
  }, [pages, run, playMedia, router, say, t]);

  // A recording's AI answer, reused for the same words instead of asking twice.
  const aiHint = useRef<{ text: string; action: CopiAiAction | null } | null>(null);

  /** Words the rules couldn't place: a name in the catalogue opens its page
   *  ("Leafnuts" → the artist), else what coπ has LEARNED for these words,
   *  else the AI decides (and is remembered unless undone), else coπ asks. */
  const understand = useCallback(async (text: string, fallbackQ?: string) => {
    say(t('looking'), 15_000);
    const known = knowledgeRef.current;
    // Names spelled the catalogue's way ("leaf nut" → "Leafnuts", nightly review).
    const spelled = applyCopiAliases(text, known.aliases);
    let lib: CopiLibrary | null = null;
    try { lib = await loadCopiLibrary(user?.uid); } catch { /* ask below */ }
    if (lib && !fallbackQ) {
      const hit = findEntity(spelled, lib);
      if (hit) { report('catalogue'); router.push(hit.href); say(t('opening', { page: hit.name })); return; }
      // Just a style's name — "jungle", "two step", "2step, the genre" — plays it.
      const asPlay = matchCopiCommand(`play ${spelled}`, pages);
      const style = asPlay?.type === 'media' ? (asPlay.genre || asPlay.q) : '';
      if (style && lib.tracks.some(tr => tr.genres.some(g => genreKey(g) === genreKey(style)))) {
        report('catalogue');
        void playMedia({ kind: 'any', genre: style });
        return;
      }
    }
    // Learned: an answer remembered for exactly these words — no AI call.
    const lang = copiLang(locale);
    const mem = lookupCopiMemory(known.memory, lang, text) || (spelled !== text ? lookupCopiMemory(known.memory, lang, spelled) : null);
    if (mem && lib) {
      if (mem.action === 'media') {
        const { action: _a, ...intent } = mem;
        if (pickMedia(intent, lib, { allowExplicit: !!user }).kind !== 'none') { report('memory'); void playMedia(intent); return; }
      } else if (applyAi(mem, lib, text)) { report('memory'); return; }
    }
    const hint = aiHint.current?.text === text ? aiHint.current : null;
    aiHint.current = null;
    const ai = hint ? hint.action : lib ? (await askCopiAi({ text: spelled }, lib, pages, user))?.action ?? null : null;
    if (ai && lib && ai.action !== 'media' && applyAi(ai, lib, text)) { report('ai'); learnLater(text, ai); return; }
    // The AI's music pick must actually exist — else ask, don't loop.
    if (ai && lib && ai.action === 'media') {
      const { action: _a, ...intent } = ai;
      if (pickMedia(intent, lib, { allowExplicit: !!user }).kind !== 'none') { report('ai'); learnLater(text, ai); void playMedia(intent); return; }
    }
    askWhat(text, fallbackQ || text, fallbackQ ? 'notFound' : 'unplaced');
  }, [say, t, user, router, pages, applyAi, playMedia, askWhat, report, learnLater, locale]);
  understandRef.current = understand;

  /** One utterance → taught phrases, rules (free), the catalogue, memory, then the AI. */
  const lastSaid = useRef('');
  /** `answer` as meant for `question`: an answer that isn't a command on its
   *  own ("2step, the genre") gets the question's verb ("play 2step, the genre"). */
  const answering = useCallback((question: string, answer: string) => {
    const alone = matchCopiCommand(answer, pages);
    const verb = leadingCopiVerb(question);
    return verb && (!alone || (alone.type === 'search' && alone.loose)) ? `${verb} ${answer}` : answer;
  }, [pages]);
  const handle = useCallback((said: string) => {
    const asked = askRef.current;
    // "no", "wrong", "fel", "non", "no es eso": the last answer was wrong —
    // ask what they meant (and never remember that AI answer). Said to an
    // open question, it just closes it.
    if (isCopiNegation(said)) {
      if (asked) { askRef.current = null; setAsk(null); setHeard(''); say(t('closed')); return; }
      const what = pendingLearn.current?.text || lastSaid.current;
      if (what) { askWhat(what, what, 'wrong'); return; }
    }
    let text = said;
    lastSaid.current = said;
    turn.current = { reported: false, taught: false };
    // Answering "What did you mean?" by voice teaches coπ.
    if (asked) {
      askRef.current = null; setAsk(null);
      // An answer that isn't a command on its own ("2step, the genre") answers
      // the original "play …" (Gordon 2026-10-31).
      text = answering(asked.heard, said);
      void teachCopi(asked.heard, text, user, pathname || '', locale);
      setHeard(t('learned', { heard: asked.heard }));
    } else {
      const taught = knowledgeRef.current.pairs.get(phraseKey(said));
      if (taught) { text = answering(said, taught); turn.current.taught = true; }
      setHeard(taught ? `${said} → ${taught}` : said);
    }
    const action = matchCopiCommand(text, pages);
    if (!action) { say(t('notHeard'), 4000); return; }
    // A new command right after an AI answer that undoes or replaces it.
    const p = pendingLearn.current;
    if (p && contradictsCopi(p.action, action)) dropPending(true);
    const aliases = knowledgeRef.current.aliases;
    if (action.type === 'media') { const { type: _type, ...intent } = action; void playMedia(aliasIntent(intent, aliases), text); return; }
    if (action.type === 'search' && action.loose) { void understand(text); return; }
    report('rules');
    run(action.type === 'search' ? { ...action, q: applyCopiAliases(action.q, aliases) } : action);
  }, [pages, playMedia, understand, run, say, t, user, pathname, answering, askWhat, dropPending, report, locale]);

  // What was heard during this press: finished phrases + the phrase still
  // being recognised. Acted on ONCE, when the button is released.
  const spoken = useRef<{ done: string[]; partial: string }>({ done: [], partial: '' });
  const finishTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finished = useRef(true);
  const handleRef = useRef(handle);
  handleRef.current = handle;

  const finish = useCallback(() => {
    if (finished.current) return;
    finished.current = true;
    if (finishTimer.current) clearTimeout(finishTimer.current);
    const text = [...spoken.current.done, spoken.current.partial].join(' ').replace(/\s+/g, ' ').trim();
    spoken.current = { done: [], partial: '' };
    if (text) handleRef.current(text);
    else say(t('notHeard'), 4000);
  }, [say, t]);

  // Recording mode (Android, or a browser without speech recognition): the
  // page keeps the mic — so the VU meters move — and Gemini writes down what
  // was said, spelling names the way the catalogue does.
  const pcm = useRef<{ chunks: Float32Array[]; rate: number; samples: number } | null>(null);
  const recordMode = useRef(false);
  /** The browser's own recogniser refused (iPhone Chrome/Firefox/Edge): record instead from now on. */
  const srRefused = useRef(false);
  /** "Microphone blocked", with the iPhone steps when that's where we are. */
  const blockedText = useCallback(() => {
    const ua = navigator.userAgent;
    if (!/iPhone|iPad|iPod/i.test(ua)) return t('micBlocked');
    const browser = /CriOS/.test(ua) ? 'Chrome' : /FxiOS/.test(ua) ? 'Firefox' : /EdgiOS/.test(ua) ? 'Edge' : 'Safari';
    return browser === 'Safari' ? t('micBlockedSafari') : t('micBlockedIos', { browser });
  }, [t]);
  const transcribe = useCallback(async (chunks: Float32Array[], rate: number) => {
    const total = chunks.reduce((n, c) => n + c.length, 0);
    if (total < rate * 0.35) { say(t('notHeard'), 4000); return; }
    say(t('looking'), 20_000);
    try {
      const lib = await loadCopiLibrary(user?.uid);
      const res = await askCopiAi({ wav: encodeWav16k(chunks, rate) }, lib, pages, user);
      const words = (res?.heard || '').trim();
      if (!words) { say(t('notHeard'), 4000); return; }
      aiHint.current = { text: words, action: res?.action ?? null };
      handleRef.current(words);
    } catch {
      say(t('noNetwork'), 5000);
    }
  }, [say, t, user, pages]);

  /** End listening. `process` lets the recogniser deliver its last words, then acts on them. */
  /**
   * Quiet the music while coπ listens (Gordon 2026-10-31: on iPhone the song
   * kept playing under the voice and coπ couldn't hear it; Android silences
   * it by itself when the mic opens). iPhone ignores `volume`, so playing
   * <audio>/<video> elements are MUTED and audible YouTube embeds are sent
   * "mute"; releasing restores exactly what was silenced. DJ decks aren't
   * page elements, so a live set never drops out.
   */
  const ducked = useRef<{ els: HTMLMediaElement[]; frames: HTMLIFrameElement[] } | null>(null);
  const duck = useCallback((on: boolean) => {
    const yt = (f: HTMLIFrameElement, func: 'mute' | 'unMute') => {
      try { f.contentWindow?.postMessage(JSON.stringify({ event: 'command', func, args: '' }), '*'); } catch { /* gone */ }
    };
    if (on) {
      if (ducked.current) return;
      const main = (window as any).__bbAudioEl as HTMLMediaElement | undefined;
      const media = new Set<HTMLMediaElement>([...(main ? [main] : []), ...Array.from(document.querySelectorAll<HTMLMediaElement>('audio, video'))]);
      const els = [...media].filter(el => !el.paused && !el.muted);
      els.forEach(el => { el.muted = true; });
      const frames = Array.from(document.querySelectorAll<HTMLIFrameElement>('iframe[src*="youtube"]'))
        .filter(f => f.dataset.bbMuted !== 'true');
      frames.forEach(f => yt(f, 'mute'));
      ducked.current = { els, frames };
    } else {
      const d = ducked.current;
      ducked.current = null;
      d?.els.forEach(el => { el.muted = false; });
      d?.frames.forEach(f => { if (f.isConnected && f.dataset.bbMuted !== 'true') yt(f, 'unMute'); });
    }
  }, []);

  const stop = useCallback((process = false) => {
    wantListen.current = false;
    duck(false);
    document.documentElement.removeAttribute('data-copi-hold');
    setListening(false);
    setLevel(0);
    (window as any).__bbMicOn = false; window.dispatchEvent(new Event('bb-mic'));
    (window as any).__bbMicDb = undefined;
    const r = rec.current;
    rec.current = null;
    const recorded = pcm.current;
    pcm.current = null;
    if (recordMode.current) {
      finished.current = true;
      if (process && recorded) void transcribe(recorded.chunks, recorded.rate);
    } else if (process && r) {
      // The recogniser's last words arrive before its `end` event (or not at
      // all on some phones — then the last interim guess is used).
      r.onend = () => finish();
      if (finishTimer.current) clearTimeout(finishTimer.current);
      finishTimer.current = setTimeout(finish, 2500);
    } else {
      finished.current = true; // a drag, a tap, or no recogniser in this browser
    }
    try { if (process) r?.stop(); else r?.abort(); } catch { /* already stopped */ }
    const a = audio.current;
    audio.current = null;
    if (a) { cancelAnimationFrame(a.raf); a.stream.getTracks().forEach(tr => tr.stop()); void a.ctx.close().catch(() => {}); }
  }, [finish, transcribe, duck]);
  const stopRef = useRef(stop);
  stopRef.current = stop;

  /** The microphone's level → window.__bbMicDb for the VU meters (and the ring). */
  const openLevel = useCallback((hasSpeech: boolean, record = false) => {
    if (audio.current || !navigator.mediaDevices?.getUserMedia) { if (record) say(t('noSpeech'), 6000); return; }
    // Recording for words: the phone's echo cancelling keeps the music playing
    // through its speaker out of what Gemini hears.
    navigator.mediaDevices.getUserMedia({ audio: record
      ? { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      : { echoCancellation: false, noiseSuppression: false, autoGainControl: false } })
      .then(stream => {
        if (!wantListen.current || audio.current) { stream.getTracks().forEach(tr => tr.stop()); return; }
        const ctx = new AudioContext();
        void ctx.resume().catch(() => {});
        const an = ctx.createAnalyser();
        an.fftSize = 1024;
        const src = ctx.createMediaStreamSource(stream);
        src.connect(an);
        if (record) {
          // Raw samples (≤ 15 s) for a WAV — Gemini doesn't take Chrome's WebM.
          const take = { chunks: [] as Float32Array[], rate: ctx.sampleRate, samples: 0 };
          pcm.current = take;
          const proc = ctx.createScriptProcessor(4096, 1, 1);
          const mute = ctx.createGain();
          mute.gain.value = 0;
          proc.onaudioprocess = (e) => {
            if (pcm.current !== take || take.samples > take.rate * 15) return;
            const ch = new Float32Array(e.inputBuffer.getChannelData(0));
            take.chunks.push(ch);
            take.samples += ch.length;
          };
          src.connect(proc);
          proc.connect(mute);
          mute.connect(ctx.destination);
        }
        const buf = new Float32Array(an.fftSize);
        const tick = () => {
          an.getFloatTimeDomainData(buf);
          let peak = 0;
          for (let i = 0; i < buf.length; i++) { const v = Math.abs(buf[i]); if (v > peak) peak = v; }
          const db = peak > 0 ? 20 * Math.log10(peak) : -120;
          (window as any).__bbMicDb = db;
          (window as any).__bbMicOn = true; window.dispatchEvent(new Event('bb-mic'));
          setLevel(Math.max(0, Math.min(1, (db + 50) / 50)));
          if (audio.current) audio.current.raf = requestAnimationFrame(tick);
        };
        audio.current = { stream, ctx, raf: requestAnimationFrame(tick) };
      })
      .catch(() => { if (!hasSpeech || record) { say(blockedText(), 9000); stopRef.current(); } });
  }, [say, t, blockedText]);

  /** Drop the level stream but keep listening (a phone that can't share the mic). */
  const closeLevel = useCallback(() => {
    const a = audio.current;
    audio.current = null;
    (window as any).__bbMicOn = false; window.dispatchEvent(new Event('bb-mic'));
    (window as any).__bbMicDb = undefined;
    setLevel(0);
    if (a) { cancelAnimationFrame(a.raf); a.stream.getTracks().forEach(tr => tr.stop()); void a.ctx.close().catch(() => {}); }
  }, []);

  // Called straight from the press (pointer or key) — no await before the
  // recogniser starts, or iOS refuses it.
  const start = useCallback(() => {
    if (wantListen.current) return;
    wantListen.current = true;
    finished.current = false;
    setHeard('');
    spoken.current = { done: [], partial: '' };
    if (finishTimer.current) clearTimeout(finishTimer.current);
    setListening(true);
    duck(true);
    hush(); // never listen to its own reading
    // Chrome on iPhone opens its own long-press menu for the link/image UNDER
    // the mic ("Open in new tab / Save in Photos" — Andrés, iPhone 12). It
    // honours -webkit-touch-callout, so callouts are off page-wide while held.
    document.documentElement.setAttribute('data-copi-hold', '');
    // No "Listening…" card (Gordon 2026-11-01: "the VU meters reacting are
    // enough"): the VU and the mic's ring show it's listening; the card only
    // comes back with the answer. Clear the last answer so nothing lingers.
    setBubble(''); setOffer(null);
    if (bubbleTimer.current) clearTimeout(bubbleTimer.current);

    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const android = /Android/i.test(navigator.userAgent);
    // iPhone browsers other than Safari have the API but Apple refuses them
    // the recogniser ("service-not-allowed" — Andrés's iPhone 12 in Chrome).
    const iosOther = /CriOS|FxiOS|EdgiOS|OPiOS/.test(navigator.userAgent);
    // Android: the phone's recogniser can't share the mic with the VU meter —
    // record instead and let Gemini listen (Gordon 2026-10-31: "Record + Gemini").
    recordMode.current = android || iosOther || srRefused.current || !SR;
    if (recordMode.current) {
      openLevel(false, true);
      return;
    }
    if (SR) {
      const r = new SR();
      r.lang = speechLang(locale);
      // One phrase per session, restarted while held: Android's continuous
      // mode repeats and drops words.
      r.continuous = false;
      r.interimResults = true;
      r.maxAlternatives = 1;
      r.onresult = (e: any) => {
        let partial = '';
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const res = e.results[i];
          const text = String(res[0]?.transcript || '').trim();
          if (!text) continue;
          if (res.isFinal) spoken.current.done.push(text);
          else partial += (partial ? ' ' : '') + text;
        }
        spoken.current.partial = partial;
      };
      r.onerror = (e: any) => {
        const err = e?.error;
        const ios = /iPhone|iPad|iPod/i.test(navigator.userAgent);
        if (err === 'service-not-allowed' || (ios && err === 'not-allowed')) {
          // The recogniser said no, not necessarily the mic: record instead
          // (now and on every later press) and let Gemini listen.
          srRefused.current = true;
          if (rec.current === r) rec.current = null;
          try { r.abort(); } catch { /* stopped */ }
          if (wantListen.current) { recordMode.current = true; closeLevel(); openLevel(false, true); }
        } else if (err === 'not-allowed') { say(blockedText(), 9000); stopRef.current(); }
        else if (err === 'network') say(t('noNetwork'), 5000);
        else if (err === 'audio-capture' && audio.current) {
          // This phone can't meter and listen at once — the words win.
          closeLevel();
          try { localStorage.setItem(PHONE_LEVEL_KEY, 'off'); } catch { /* storage blocked */ }
        }
      };
      // A phrase ended (or a pause) while the button is still held: keep the
      // words, listen for more.
      r.onend = () => {
        if (!wantListen.current || rec.current !== r) return;
        const p = spoken.current.partial;
        if (p) { spoken.current.done.push(p); spoken.current.partial = ''; }
        setTimeout(() => { if (wantListen.current && rec.current === r) { try { r.start(); } catch { /* restarting */ } } }, 60);
      };
      rec.current = r;
      try { r.start(); } catch { /* already running */ }
    } else {
      say(t('noSpeech'), 6000);
    }

    // The room level for the main VU. Desktop: at once. iPhone/iPad: once
    // the recogniser has the mic, and never again on one that can't share it.
    // Android: never while listening (it takes the mic from the recogniser).
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    let phoneOff = false;
    try { phoneOff = localStorage.getItem(PHONE_LEVEL_KEY) === 'off'; } catch { /* storage blocked */ }
    if (!coarse) openLevel(true);
    else if (!phoneOff && rec.current) rec.current.onaudiostart = () => openLevel(true);
  }, [locale, say, t, openLevel, closeLevel, blockedText, duck, hush]);

  useEffect(() => () => stopRef.current(), []);

  // iPad/iPhone: a long press started text selection and the "Save to
  // Photos / Copy image" menu on the mic (Gordon 2026-10-31). Pointer events
  // can't cancel that — only a non-passive touchstart can. Holding and
  // dragging still work: they run on pointer events, not click.
  const micShown = !!pos && !fullscreen;
  useEffect(() => {
    const el = btnRef.current;
    if (!el) return;
    const block = (e: TouchEvent) => { if (e.cancelable) e.preventDefault(); };
    el.addEventListener('touchstart', block, { passive: false });
    return () => el.removeEventListener('touchstart', block);
  }, [micShown]);

  // Hold M to talk on a keyboard (not while typing).
  useEffect(() => {
    const typing = (el: EventTarget | null) => {
      const n = el as HTMLElement | null;
      return !!n && (n.tagName === 'INPUT' || n.tagName === 'TEXTAREA' || n.tagName === 'SELECT' || n.isContentEditable);
    };
    const down = (e: KeyboardEvent) => {
      if ((e.key === 'm' || e.key === 'M') && !e.repeat && !e.metaKey && !e.ctrlKey && !e.altKey && !typing(e.target)) { e.preventDefault(); start(); }
    };
    const up = (e: KeyboardEvent) => { if ((e.key === 'm' || e.key === 'M') && wantListen.current) stopRef.current(true); };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, [start]);

  // Press = talk (until release); move = drag the button instead.
  const pressedAt = useRef(0);
  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!pos || (e.pointerType === 'mouse' && e.button !== 0)) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    // Where it really is now (an anchored mic is placed from the bottom).
    const top = e.currentTarget.getBoundingClientRect().top;
    drag.current = { dx: e.clientX - pos.x, dy: e.clientY - top, x0: e.clientX, y0: e.clientY, moved: false };
    pressedAt.current = Date.now();
    start();
  };
  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d) return;
    if (!d.moved) {
      // A finger wobbles while it holds (Gordon 2026-10-31: "a little dodgy and
      // jumps out" on Android): on touch, only a quick, clear move is a drag —
      // once you're holding still and talking, small shifts keep talking.
      const touch = e.pointerType !== 'mouse';
      if (Math.hypot(e.clientX - d.x0, e.clientY - d.y0) < (touch ? 16 : 8)) return;
      if (touch && Date.now() - pressedAt.current > 450) return;
    }
    if (!d.moved) { d.moved = true; stop(false); setBubble(''); setAnchorBottom(null); } // it's a drag, not a voice message
    setPos(clamp(e.clientX - d.dx, e.clientY - d.dy));
  };
  const onPointerUp = () => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (d.moved) {
      anchored.current = false;
      setPos(cur => {
        if (cur) {
          // Lower half: remember it from the bottom (and place it that way), upper half from the top.
          const lower = cur.y + sizeRef.current / 2 > window.innerHeight / 2;
          const bottom = Math.round(window.innerHeight - cur.y - sizeRef.current);
          setAnchorBottom(lower ? bottom : null);
          try { localStorage.setItem(POS_KEY, JSON.stringify(lower ? { x: cur.x, bottom } : cur)); } catch { /* storage blocked */ }
        }
        return cur;
      });
      return;
    }
    if (Date.now() - pressedAt.current < 300) { stop(false); say(t('holdHint'), 3500); return; }
    stop(true);
  };

  /** Typed answer to "What did you mean?" — remembered, then done. */
  const submitMeant = () => {
    const a = askRef.current;
    const m = meant.trim();
    if (!a || !m) return;
    // A pasted Discogs link is an order, not a meaning to learn.
    const link = DISCOGS_LINK.exec(m)?.[0];
    if (link) { closeAsk(); setMeant(''); void importFromDiscogs(link); return; }
    handle(m); // handle() sees the open question and teaches coπ
  };

  if (!pos || fullscreen) return null;
  // The mic's top right now (from the bottom while it sits over the chat button).
  const topNow = anchorBottom !== null ? window.innerHeight - anchorBottom - size : pos.y;
  const bubbleLeft = pos.x > window.innerWidth / 2;
  // Beside the mic, as wide as the room there allows (≤ 14rem — a little
  // taller than wide, Gordon 2026-10-31); if there's no room beside it,
  // across the screen with an 8px margin.
  const room = bubbleLeft ? pos.x - 16 : window.innerWidth - (pos.x + size) - 16;
  const cardW = Math.min(224, room);
  const cardPos = cardW >= 180
    ? { top: Math.max(8, topNow - 4), width: cardW, ...(bubbleLeft ? { right: window.innerWidth - pos.x + 8 } : { left: pos.x + size + 8 }) }
    : { top: Math.max(8, topNow - 4), left: 8, right: 8 };
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        data-bb-copi-mic=""
        aria-label={t('holdHint')}
        aria-pressed={listening}
        title={t('holdHint')}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => { drag.current = null; if (wantListen.current) stop(false); }}
        onContextMenu={e => e.preventDefault()}
        // Drop a Discogs link on coπ to import its credits.
        onDragOver={e => { if ([...e.dataTransfer.types].some(x => x === 'text/uri-list' || x === 'text/plain')) e.preventDefault(); }}
        onDrop={e => {
          const link = DISCOGS_LINK.exec(e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/plain'))?.[0];
          if (!link) return;
          e.preventDefault();
          void importFromDiscogs(link);
        }}
        onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && !e.repeat) { e.preventDefault(); start(); } }}
        onKeyUp={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); stop(true); } }}
        className={cn(
          // bb-circle: opts out of the theme's button radius — coπ is always round.
          'bb-circle fixed z-[110] flex flex-col items-center justify-center rounded-full border-2 border-primary text-primary',
          // Theme-background glass, not a primary tint: a white primary on a white
          // /launch slide made the whole button vanish (Gordon 2026-11-01).
          'bg-background/75 shadow-lg backdrop-blur-md transition-shadow select-none touch-none [-webkit-touch-callout:none]',
          listening && 'shadow-[0_0_0_4px_hsl(var(--primary)/0.35)]',
        )}
        style={{ left: pos.x, ...(anchorBottom !== null ? { bottom: anchorBottom } : { top: pos.y }), width: size, height: size, WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}
      >
        {/* Held: the ring breathes with the microphone level (desktop) or
            pulses (phones, where the level stream is skipped). */}
        {listening && (
          <span aria-hidden="true" className={cn('pointer-events-none absolute inset-0 rounded-full border-2 border-primary', level === 0 && 'animate-ping')}
            style={level > 0 ? { transform: `scale(${1 + level * 0.45})`, opacity: 0.25 + level * 0.6 } : undefined} />
        )}
        {/* Icon + label never take the touch — iOS treats a pressed <svg> as an image. */}
        <VintageMic className="pointer-events-none h-6 w-6" />
        <span className="pointer-events-none text-3xs font-bold leading-none">coπ</span>
      </button>
      {ask ? (
        <div role="dialog" aria-label={t('whatDidYouMean')}
          className="fixed z-[110] rounded-xl border-2 border-primary bg-background/95 p-3 text-sm text-foreground shadow-lg backdrop-blur-md"
          style={{ ...cardPos, top: Math.max(8, Math.min(topNow - 4, window.innerHeight - 330)) }}>
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <span className="block truncate text-2xs text-muted-foreground">“{ask.heard}”</span>
              <span className="font-semibold">{t('whatDidYouMean')}</span>
            </div>
            <button type="button" onClick={closeAsk} aria-label={tCommon('close')} className="-mr-1 -mt-1 rounded p-1 text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </div>
          {/* Stacked, so the card is taller than wide and fits beside the mic. */}
          <form className="mt-2 flex flex-col gap-2" onSubmit={e => { e.preventDefault(); submitMeant(); }}>
            <input value={meant} onChange={e => setMeant(e.target.value)} placeholder={t('meantPlaceholder')}
              enterKeyHint="done" autoComplete="off" autoCorrect="off" spellCheck={false} aria-label={t('whatDidYouMean')}
              className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm" />
            <button type="submit" disabled={!meant.trim()} className="w-full rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">
              {t('teach')}
            </button>
          </form>
          <button type="button" onClick={() => { const q = ask.q; closeAsk(); run({ type: 'search', q }); }}
            className="mt-2 flex w-full items-center gap-2 rounded-md border border-border px-2 py-1.5 text-left text-sm hover:bg-secondary">
            <Search className="h-4 w-4 shrink-0" /> <span className="line-clamp-2 break-words">{t('searchFor', { q: ask.q })}</span>
          </button>
          <p className="mt-2 text-2xs text-muted-foreground">{t('sayItAgain')}</p>
        </div>
      ) : bubble && (
        <div role="status" aria-live="polite"
          className="pointer-events-none fixed z-[110] rounded-xl border-2 border-primary bg-background/90 px-3 py-2 text-sm text-foreground shadow-lg backdrop-blur-md"
          style={{ ...cardPos, width: undefined, maxWidth: 'width' in cardPos ? cardPos.width : undefined }}>
          {heard && <span className="block text-2xs text-muted-foreground">“{heard}”</span>}
          {bubble}
          {/* After an answer: one tap to say it was wrong and teach coπ. */}
          {offer && (
            <button type="button" onClick={() => { const o = offer; setOffer(null); o.run(); }}
              className="pointer-events-auto mt-1.5 block w-full rounded-md bg-primary px-2 py-1 text-left text-xs font-semibold text-primary-foreground">
              {offer.label}
            </button>
          )}
          {offer?.alt && (
            <button type="button" onClick={() => { const a = offer.alt!; setOffer(null); a.run(); }}
              className="pointer-events-auto mt-1 block w-full rounded-md border border-foreground px-2 py-1 text-left text-xs font-semibold text-foreground">
              {offer.alt.label}
            </button>
          )}
          {heard && !listening && (
            <button type="button" onClick={() => askWhat(lastSaid.current, lastSaid.current, 'wrong')}
              className="pointer-events-auto mt-1 block text-2xs font-semibold text-primary underline underline-offset-2">
              {t('notWhatYouMeant')}
            </button>
          )}
        </div>
      )}
    </>
  );
}
